/**
 * useRecordProcedureRuns — stan zadań przycisku SQL na karcie rekordu + uruchamianie.
 *
 * Odpytuje `GET /record-procedures/runs` o aktywne zadanie rekordu i ostatnie zadanie każdej procedury:
 *   - jeden odczyt przy wejściu na kartę (i po akcjach: zlecenie, odrzucony zapis, powrót do karty),
 *   - odpytywanie co ~2 s WYŁĄCZNIE gdy jest aktywne zadanie (`active !== null`); bez aktywnego — cisza.
 *     Zapytanie w stanie błędu (np. restart serwera przy wdrożeniu) jest odpytywane rzadziej, co ~10 s;
 *     hak wystawia wtedy `isError` (pasek blokady pokazuje informację), a po udanym odczycie wraca
 *     zwykły odstęp,
 *   - po przejściu zadania w stan końcowy (widać to jako zniknięcie `active`): komunikat w rogu
 *     (sukces z napisem wyniku, błąd, przerwanie) i wywołanie `onFinished` — strona odświeża wtedy
 *     dane rekordu, bo zapytanie mogło go zmienić. Komunikat dostaje każdy, kto ma kartę otwartą
 *     (także gdy zadanie zlecił ktoś inny); zadanie zakończone przed otwarciem karty niczego nie ogłasza.
 *
 * Błąd odczytu stanu nie wyświetla komunikatu w rogu (przycisk i pasek po prostu się nie pojawiają) —
 * komunikaty dotyczą zleceń i odrzuconych zapisów; jedyny ślad błędu to `isError` (patrz wyżej).
 *
 * `startRun` zleca zadanie i NIGDY nie rzuca: odmowy serwera (brak prawa, procedura niedostępna, zadanie
 * już trwa) zgłasza komunikatem w rogu i zwraca `null` — wołający (przycisk) nie obsługuje błędów sam.
 * Przyjmuje opcjonalne id rekordu, bo przy tworzeniu karta dopiero po zapisie zna id nowego rekordu.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useToast } from '@/contexts/ToastContext';
import { runConflictText, runLabel, runResultText } from '@/lib/record-procedures/run-texts';
import {
  extractRunConflict,
  fetchRecordRuns,
  isActiveRunStatus,
  type RecordProcedureRun,
  recordRunsKey,
  type RunConflict,
  type RunnableRecordType,
  type StartedRecordRun,
  startRecordRun,
} from '@/lib/record-procedures/runs-api';

/** Odstęp odpytywania stanu, gdy rekord ma aktywne zadanie. */
export const RECORD_RUNS_POLL_INTERVAL_MS = 2000;
/** Odstęp odpytywania, gdy ostatni odczyt stanu się nie udał (np. restart serwera przy wdrożeniu). */
export const RECORD_RUNS_ERROR_POLL_INTERVAL_MS = 10_000;

const NO_LABELS: ReadonlyMap<string, string> = new Map();

export interface UseRecordProcedureRunsOptions {
  /** Tylko dla dokumentów — klasa dokumentu. */
  recordClass?: string;
  /** Napisy przycisków z układu wg id procedury — do komunikatów („<napis>: …”) i paska blokady. */
  labels?: ReadonlyMap<string, string>;
  /** Wołane raz po przejściu zadania w stan końcowy — strona odświeża dane rekordu. */
  onFinished?: (run: RecordProcedureRun) => void;
}

export interface UseRecordProcedureRunsResult {
  /** Zadanie w kolejce / w toku na tym rekordzie (blokuje zapis) albo null. */
  active: RecordProcedureRun | null;
  /** Ostatnie zadanie każdej procedury na rekordzie (najnowsze pierwsze, z aktywnym). */
  lastByProcedure: readonly RecordProcedureRun[];
  isLoading: boolean;
  /**
   * Ostatni odczyt stanu zadań się nie udał (odpytywanie jest wtedy wstrzymane) — widoczny stan
   * (np. pasek blokady) może być nieaktualny.
   */
  isError: boolean;
  /** Trwa wysyłka zlecenia (od startu do odpowiedzi i odświeżenia stanu). */
  starting: boolean;
  /**
   * Zleca zadanie procedury na rekordzie (domyślnie bieżącym, `targetRecordId` = świeżo utworzony;
   * `companyId` = firma zlecenia, gdy ma być inna niż aktywna — np. firma wybrana w formularzu tworzenia).
   * Zwraca przyjęte zlecenie (wiersz zadania bez nazw — patrz `StartedRecordRun`) albo `null` po odmowie
   * (komunikat już pokazany).
   */
  startRun: (
    procedureId: string,
    targetRecordId?: string,
    companyId?: string,
  ) => Promise<StartedRecordRun | null>;
  /** Odświeża stan zadań rekordu. */
  refresh: () => Promise<void>;
  /**
   * Błąd zapisu / usunięcia rekordu: gdy to blokada na czas zadania (`RECORD_LOCKED_BY_RUN`) — pokazuje
   * czytelny komunikat, odświeża stan (pojawi się pasek) i zwraca `true`; inaczej `false` (wołający
   * pokazuje swój zwykły komunikat błędu).
   */
  handleLockedError: (err: unknown) => boolean;
  /** Napis przycisku zadania (z układu) albo nazwa procedury. */
  labelFor: (run: RecordProcedureRun) => string;
}

export function useRecordProcedureRuns(
  recordType: RunnableRecordType,
  recordId: string | null | undefined,
  options: UseRecordProcedureRunsOptions = {},
): UseRecordProcedureRunsResult {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { recordClass } = options;
  const labels = options.labels ?? NO_LABELS;
  const key = useMemo(
    () => recordRunsKey(recordType, recordId, recordClass),
    [recordType, recordId, recordClass],
  );
  const [starting, setStarting] = useState(false);

  // Najnowsze zależności dla efektów i zwrotek asynchronicznych (bez ich w tablicach deps).
  const latest = useRef({
    t,
    language: i18n.language,
    toast,
    labels,
    onFinished: options.onFinished,
  });
  useEffect(() => {
    latest.current = { t, language: i18n.language, toast, labels, onFinished: options.onFinished };
  });

  const query = useQuery({
    queryKey: key,
    queryFn: () => {
      if (!recordId) throw new Error('useRecordProcedureRuns: odczyt stanu bez rekordu');
      return fetchRecordRuns({ recordType, recordId, recordClass });
    },
    enabled: !!recordId,
    // Błąd nie zatrzymuje odpytywania, tylko je rzedzi (ostatnie dane zostają na ekranie, `isError` je
    // oznacza) — inaczej po chwilowym błędzie karta zostałaby zablokowana do przeładowania strony.
    refetchInterval: (q) => {
      if (!q.state.data?.active) return false;
      return q.state.status === 'error'
        ? RECORD_RUNS_ERROR_POLL_INTERVAL_MS
        : RECORD_RUNS_POLL_INTERVAL_MS;
    },
  });

  // Zadanie, którego koniec ogłosimy: widziane jako aktywne albo zlecone z tej karty. Zbiór ogłoszonych
  // chroni przed podwójnym komunikatem (np. podwójne uruchomienie efektu w trybie deweloperskim).
  const trackedRef = useRef<string | null>(null);
  const announcedRef = useRef<Set<string>>(new Set());
  // Zmiana rekordu (ta sama strona, inny rekord) zaczyna śledzenie od zera — deklarowany PRZED efektem
  // danych, żeby w tym samym zatwierdzeniu najpierw zerował, potem czytał dane nowego rekordu.
  useEffect(() => {
    trackedRef.current = null;
    announcedRef.current = new Set();
  }, [recordType, recordId, recordClass]);

  const data = query.data;
  useEffect(() => {
    if (!data) return;
    const tracked = trackedRef.current;
    if (tracked !== null && data.active?.id !== tracked) {
      trackedRef.current = null;
      const finished = data.lastByProcedure.find(
        (run) => run.id === tracked && !isActiveRunStatus(run.status),
      );
      if (finished && !announcedRef.current.has(finished.id)) {
        announcedRef.current.add(finished.id);
        const { t: tr, toast: toastApi, labels: labelMap, onFinished } = latest.current;
        const label = runLabel(finished, labelMap);
        const text = runResultText(finished, label, tr);
        if (finished.status === 'done') {
          toastApi.show({
            variant: 'success',
            title: tr('recordProcedureRuns.toast.done', { label, text }),
          });
        } else {
          toastApi.show({
            variant: finished.status === 'failed' ? 'error' : 'warning',
            title: label,
            description: text,
          });
        }
        onFinished?.(finished);
      }
    }
    if (data.active) trackedRef.current = data.active.id;
  }, [data]);

  const refresh = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, key]);

  const reportConflict = useCallback((conflict: RunConflict, variant: 'error' | 'info'): void => {
    const { t: tr, toast: toastApi, labels: labelMap, language } = latest.current;
    toastApi.show({ variant, title: runConflictText(conflict, labelMap, tr, language) });
  }, []);

  const startRun = useCallback(
    async (
      procedureId: string,
      targetRecordId?: string,
      companyId?: string,
    ): Promise<StartedRecordRun | null> => {
      const targetId = targetRecordId ?? recordId;
      if (!targetId) {
        throw new Error(
          'useRecordProcedureRuns.startRun: brak rekordu, na którym uruchomić zadanie',
        );
      }
      setStarting(true);
      try {
        const run = await startRecordRun(
          procedureId,
          { recordType, recordId: targetId, recordClass },
          companyId,
        );
        if (targetId === recordId) trackedRef.current = run.id;
        // Stan zadań (kolejka / w toku) widać od razu — przycisk nie mignie stanem „gotowy”.
        await queryClient.invalidateQueries({
          queryKey: recordRunsKey(recordType, targetId, recordClass),
        });
        return run;
      } catch (err) {
        const conflict = extractRunConflict(err);
        if (conflict !== null) {
          reportConflict(conflict, conflict.code === 'RUN_IN_PROGRESS' ? 'info' : 'error');
          // Zadanie już trwa (ktoś był pierwszy) — pokaż jego stan zamiast przycisku „gotowy”.
          if (conflict.code === 'RUN_IN_PROGRESS') {
            await queryClient.invalidateQueries({
              queryKey: recordRunsKey(recordType, targetId, recordClass),
            });
          }
        } else if (isAxiosError(err) && err.response?.status === 403) {
          reportConflict({ code: 'PERMISSION_DENIED', activeRun: null, message: null }, 'error');
        } else {
          const { t: tr, toast: toastApi } = latest.current;
          toastApi.show({ variant: 'error', title: tr('recordProcedureRuns.errors.startFailed') });
        }
        return null;
      } finally {
        setStarting(false);
      }
    },
    [recordId, recordType, recordClass, queryClient, reportConflict],
  );

  const handleLockedError = useCallback(
    (err: unknown): boolean => {
      const conflict = extractRunConflict(err);
      if (conflict?.code !== 'RECORD_LOCKED_BY_RUN') return false;
      reportConflict(conflict, 'error');
      void refresh();
      return true;
    },
    [reportConflict, refresh],
  );

  const labelFor = useCallback(
    (run: RecordProcedureRun): string => runLabel(run, labels),
    [labels],
  );

  return {
    active: data?.active ?? null,
    lastByProcedure: data?.lastByProcedure ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    starting,
    startRun,
    refresh,
    handleLockedError,
    labelFor,
  };
}
